import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import backgroundImage from "./assets/bg-image.png";
import titleRobo from "./assets/zh/title.jpg";
import section1 from "./assets/zh/section-1.png";
import roboShare from "./assets/zh/robo-share.png";
import roboComment from "./assets/zh/robo-comment.png";
import theme from "./assets/zh/theme.png";
import summary from "./assets/zh/summary.png";
import googlePlayBanner from "./assets/en/google-play-banner.png";
import appStoreBanner from "./assets/en/app-store-banner.svg";
import iconLock from "./assets/icon-lock.png";
import iconBackup from "./assets/icon-backup.png";
import iconSearch from "./assets/icon-search.png";
import iconFree from "./assets/icon-free.png";

import "./TopPage.scss";
import { Helmet } from "react-helmet";

import ReactGA from "react-ga4";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=net.aidiary";
const APP_STORE_URL = "https://apps.apple.com/jp/app/id6736706558";

function TopPageZh() {
  const navigate = useNavigate();
  const search = useLocation().search;
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const page = query.get("page");
  const tutorial = query.get("tutorial");
  const [visibleSections, setVisibleSections] = useState<Set<number>>(
    new Set(),
  );
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: TopPageZh.name,
      title: TopPageZh.name,
    });
  }, []);

  useEffect(() => {
    // /?page=pp の場合 /ppにリダイレクト
    if (page) {
      console.log("page", page);
      navigate("/" + page);
    }
  }, [navigate, page, query]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    sectionRefs.current.forEach((section, index) => {
      if (!section) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleSections((prev) => new Set(prev).add(index));
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: "0px 0px -50px 0px",
        },
      );

      observer.observe(section);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <div
      className="TopPage-wrap"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundRepeat: "repeat",
        backgroundPosition: "0 0",
        backgroundSize: "auto",
      }}
    >
      <div
        className="App"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundRepeat: "repeat",
          backgroundPosition: "0 0",
          backgroundSize: "auto",
        }}
      >
        <Helmet>
          <title>AI日記 Kirono:可愛、簡單、連結。全新的日記習慣</title>
          <meta
            name="description"
            content="一款 AI 驅動的日記 App，最快只需 1 分鐘即可寫出專業水準的日記。支援交換日記、自動備份、圖形鎖安全保護，而且完全免費！"
          />
          <meta http-equiv="Content-Script-Type" content="text/javascript" />
          <meta http-equiv="Content-Style-Type" content="text/css" />

          <meta
            property="og:title"
            content="AI日記 Kirono:可愛、簡單、連結。全新的日記習慣"
          />
          <meta
            property="og:description"
            content="想把珍貴的每一天美好地保存下來，卻總是忙到沒時間嗎？AI 日記會為你代筆，生成附有精美圖片的日記內容！同時還具備圖形鎖、交換日記等豐富功能。"
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.ai-diary.jp/" />
          <meta
            property="og:image"
            content="https://www.ai-diary.jp/image/zh/thumbnail.png"
          />
          <meta
            property="og:site_name"
            content="AI日記 Kirono:可愛、簡單、連結。全新的日記習慣"
          />
          <meta property="og:locale" content="zh_TW" />

          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" />
          <link
            href="https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=M+PLUS+2:wght@100..900&family=Montserrat:ital,wght@0,100..900;1,100..900&family=Nobile:ital,wght@0,400;0,500;0,700;1,400;1,500;1,700&family=Noto+Sans+JP:wght@100..900&family=Orbitron:wght@400..900&family=Petrona:ital,wght@0,100..900;1,100..900&family=Zen+Maru+Gothic&display=swap"
            rel="stylesheet"
          />
        </Helmet>

        {tutorial !== "true" && (
          <div className="language-area">
            <a href="/">日本語</a>
            <p>/</p>
            <a href="/en">English</a>
            <p>/</p>
            <a href="/zh">中文</a>
          </div>
        )}

        <div
          className="title-area"
          style={{
            backgroundImage: `url(${titleRobo})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <p className="sub-title">
            用美麗的文字與圖片
            <br />
            保存你珍貴的每一天的 AI 日記 App
          </p>
        </div>

        {tutorial !== "true" && (
          <div className="store-image-wrapper">
            <img
              src={googlePlayBanner}
              onClick={() => window.open(GOOGLE_PLAY_URL, "_blank")}
              className="google-playbanner-image"
              alt="Google Play"
            />
            <img
              src={appStoreBanner}
              onClick={() => window.open(APP_STORE_URL, "_blank")}
              className="google-playbanner-image"
              alt="App Store"
            />
          </div>
        )}

        <div
          className={`section scroll-animate ${
            visibleSections.has(0) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[0] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="①"
            sub="讓你的日常生活閃耀光彩！"
            title="AI 日記生成"
          />
          <img
            src={section1}
            className="section-image trim-vertical"
            alt="AI 日記生成"
          />
          <div className="description-wrapper">
            <ul>
              <li>只要和 AI 聊天或回答簡單問題，就能輕鬆完成日記</li>
              <li>AI 也能根據日記內容生成精美圖片</li>
              <li>透過 AI 語音朗讀，彷彿在聆聽故事般的體驗</li>
            </ul>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(1) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[1] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="②"
            sub="還能收到留言！"
            title="AI 會為你的日記留言"
          />
          <img src={roboComment} className="section-image" alt="AI 日記留言" />
          <div className="description-wrapper">
            <ul>
              <li>來自 AI 角色的留言，讓你更有動力持續寫日記</li>
              <li>當你煩惱時，AI 也可能給你轉換心情的建議！</li>
            </ul>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(2) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[2] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="③"
            sub="交換日記也很簡單！"
            title="與朋友或大家分享日記"
          />
          <img src={roboShare} className="section-image" alt="分享日記" />
          <div className="description-wrapper">
            <ul>
              <li>可將日記分享給朋友或公開給大家</li>
              <li>可設定公開範圍，完善保護隱私</li>
            </ul>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(3) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[3] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="④"
            sub="打造專屬於你的日記！"
            title="字體與背景自訂"
          />
          <img src={theme} className="section-image trim-top" alt="自訂樣式" />
          <div className="description-wrapper">
            <ul>
              <li>可自由設定字體、文字顏色、大小、背景顏色與背景圖片</li>
            </ul>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(4) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[4] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="⑤"
            sub="每週回顧也很輕鬆！"
            title="一週總結功能"
          />
          <div className="section-image-description-row">
            <img src={summary} className="left-image" alt="一週總結" />
            <div className="description-wrapper">
              <ul>
                <li>每週寫日記達 3 次以上，AI 就會為你整理一整週的回顧</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(5) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[5] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="⑥"
            sub="好好守護重要的日記！"
            title="完善的安全功能"
          />
          <div className="section-image-description-row">
            <img src={iconLock} className="left-image" alt="安全功能" />
            <div className="description-wrapper">
              <ul>
                <li>螢幕鎖定功能，防止他人偷看你的日記</li>
                <li>分享到社群平台時，AI 會自動匿名化個人姓名</li>
                <li>與伺服器的通訊全程加密</li>
                <li>無需輸入任何個人資訊</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(6) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[6] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="⑦"
            sub="換手機也安心！"
            title="日記自動備份"
          />
          <div className="section-image-description-row">
            <img src={iconBackup} className="left-image" alt="自動備份" />
            <div className="description-wrapper">
              <ul>
                <li>日記儲存在伺服器中，即使遺失手機或更換裝置也不會消失</li>
                <li>使用批次分享功能，也能輕鬆保存到 Google Keep 等 App</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(7) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[7] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="⑧"
            sub="那天的日記也能立刻找到！"
            title="強大的搜尋功能"
          />
          <div className="section-image-description-row">
            <img src={iconSearch} className="left-image" alt="搜尋功能" />
            <div className="description-wrapper">
              <ul>
                <li>可快速搜尋並找到任何過去的日記內容</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(8) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[8] = el)}
        >
          <TitleLabel
            label="功能特色"
            number="⑨"
            sub="免費使用！"
            title="所有基本功能完全免費"
          />
          <div className="section-image-description-row">
            <img src={iconFree} className="left-image" alt="免費" />
            <div className="description-wrapper">
              <ul>
                <li>日記撰寫、AI 對話、圖片生成等基本功能皆可免費使用</li>
                <li>每天最多可免費撰寫 5 篇日記</li>
                <li>另有提供給重度使用者的進階付費方案</li>
              </ul>
            </div>
          </div>
        </div>

        <div
          className={`section scroll-animate ${
            visibleSections.has(9) ? "animate-in" : ""
          }`}
          ref={(el) => (sectionRefs.current[9] = el)}
        >
          <TitleLabel title="使用注意事項" />
          <div className="description-wrapper">
            <ul>
              <li>
                本 App 使用 AI
                生成日記內容，生成結果不一定完全正確，請確認內容後再進行保存或分享。
              </li>

              <li>使用前請務必閱讀並同意使用條款與隱私權政策。</li>
            </ul>
          </div>

          {tutorial !== "true" && (
            <div className="tosppWrapper">
              <Link to="/tos">使用條款</Link>・
              <Link to="/privacy">隱私權政策</Link>・
              <Link to="/deletedata">資料刪除方式</Link>
            </div>
          )}
        </div>

        {tutorial !== "true" && (
          <div
            className={`section scroll-animate ${
              visibleSections.has(10) ? "animate-in" : ""
            }`}
            ref={(el) => (sectionRefs.current[10] = el)}
          >
            <TitleLabel title="聯絡我們" />
            <div className="description-wrapper">
              <a href="mailto:support@ai-daiary.net">support@ai-daiary.net</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type TitleLabelProps = {
  label?: string;
  number?: string;
  sub?: string;
  title: string;
};
const TitleLabel = (props: TitleLabelProps) => {
  return (
    <div className="section-title-wrapper">
      {props.label && (
        <p className="feature-label">
          {props.label}
          <span>{props.number}</span>
        </p>
      )}
      <div className="section-title-right-wrapper">
        {props.sub && <p className="section-title-sub">{props.sub}</p>}
        <p className="title">{props.title}</p>
      </div>
    </div>
  );
};

export default TopPageZh;
